import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { ROSTER_ROLES, formatDateTime, formatDay, todayUk } from '@nu/shared';
import Modal from '../../components/Modal.jsx';
import { api } from '../../api/client.js';
import { problem } from './util.js';

// The pop-ups behind the Match-Ups table. Each gets `change(fn, okText)`,
// which runs the request, shows the result on the page and reloads.

function Footer({ onClose, busy, disabled, label, danger, form, onClick }) {
  return (
    <>
      <button type="button" className="btn" onClick={onClose}>Cancel</button>
      <button type={form ? 'submit' : 'button'} form={form} className={`btn ${danger ? 'danger-btn' : 'primary'}`} disabled={busy || disabled} onClick={onClick}>{label}</button>
    </>
  );
}

function useSubmit(fn) {
  const [busy, setBusy] = useState(false);
  return [busy, async (...args) => { setBusy(true); try { await fn(...args); } finally { setBusy(false); } }];
}

export function AddPersonModal({ bands, change, onClose }) {
  const [candidates, setCandidates] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [id, setId] = useState('');
  const [role, setRole] = useState('installer');
  const [bandId, setBandId] = useState(String(bands.find((b) => b.active)?.id || ''));
  useEffect(() => {
    api('/matchups/candidates').then((r) => setCandidates(r.people), (e) => setError(problem(e)));
  }, []);
  const picked = candidates?.find((c) => c.id === id);
  const term = q.trim().toLowerCase();
  const shown = (candidates || []).filter((c) => !term || c.name.toLowerCase().includes(term) || c.email.includes(term));
  const [busy, submit] = useSubmit(async () => {
    const ok = await change(
      () => api('/matchups/people', { method: 'POST', body: { id, role, bandId: role === 'installer' && bandId ? Number(bandId) : null } }),
      `${picked.name} added. Set their team, van and home postcode in the table.`,
    );
    if (ok) onClose();
  });
  return (
    <Modal open title="Add person" onClose={onClose} footer={<Footer onClose={onClose} busy={busy} disabled={!id} label="Add" onClick={submit} />}>
      <div className="form">
        {error && <p className="notice-line notice-bad">{error}</p>}
        {!error && !candidates && <p className="muted small">Loading people with access…</p>}
        {candidates && !candidates.length && (
          <p className="muted">Everyone with Nano Utilities access is already in Match-Ups. Give someone access on the Permissions page first.</p>
        )}
        {candidates?.length > 0 && (
          <>
            <label className="search">
              <Search size={18} aria-hidden="true" />
              <input type="search" placeholder="Filter by name or email" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
            </label>
            <ul className="pick-list short">
              {shown.map((c) => (
                <li key={c.id}>
                  <button type="button" className={`pick${id === c.id ? ' picked' : ''}`} onClick={() => setId(c.id)}>
                    <strong>{c.name}</strong>
                    <span className="muted small">{c.email}{c.jobTitle ? ` · ${c.jobTitle}` : ''}</span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="fld-row">
              <label className="fld">
                Role
                <select value={role} onChange={(e) => setRole(e.target.value)}>
                  {ROSTER_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </label>
              {role === 'installer' && (
                <label className="fld">
                  Band
                  <select value={bandId} onChange={(e) => setBandId(e.target.value)}>
                    <option value="">No band</option>
                    {bands.filter((b) => b.active).map((b) => <option key={b.id} value={b.id}>{b.name} ({b.dailyTarget} a day)</option>)}
                  </select>
                </label>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// Override an engineer's band target, with a reason; blank goes back to the band.
export function PersonTargetModal({ person, bands, change, onClose }) {
  const [target, setTarget] = useState(person.targetOverride ?? '');
  const [reason, setReason] = useState(person.targetOverrideReason || '');
  const band = bands.get(person.bandId);
  const [busy, submit] = useSubmit(async (e) => {
    e.preventDefault();
    const ok = await change(
      () => api(`/matchups/people/${person.id}`, { method: 'PUT', body: { targetOverride: target === '' ? null : Number(target), targetOverrideReason: reason } }),
      target === '' ? `${person.name} is back on their band's target.` : `${person.name}'s target is ${target} a day.`,
    );
    if (ok) onClose();
  });
  return (
    <Modal open title={`${person.name}: daily target`} onClose={onClose} footer={<Footer onClose={onClose} busy={busy} label="Save" form="ptarget" />}>
      <form id="ptarget" className="form" onSubmit={submit}>
        <p className="muted small">{band ? `${band.name} band: ${band.dailyTarget} a day.` : 'No band, so no target unless you set one here.'} Leave blank to use the band.</p>
        <div className="fld-row">
          <label className="fld narrow">Installs a day<input type="number" min="0" max="20" step="0.5" value={target} onChange={(e) => setTarget(e.target.value)} autoFocus /></label>
          <label className="fld">Reason<input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={255} disabled={target === ''} required={target !== ''} /></label>
        </div>
      </form>
    </Modal>
  );
}

// Rename, override the target, or close a team.
export function TeamModal({ team, lookups, change, onClose }) {
  const [name, setName] = useState(team.name);
  const [target, setTarget] = useState(team.targetOverride ?? '');
  const [reason, setReason] = useState(team.targetOverrideReason || '');
  const [active, setActive] = useState(team.active);
  const sum = team.members.map((m) => lookups.people.get(m.personId)?.target.value ?? 0);
  const [busy, submit] = useSubmit(async (e) => {
    e.preventDefault();
    const ok = await change(
      () => api(`/matchups/teams/${team.id}`, { method: 'PUT', body: { name, targetOverride: target === '' ? null : Number(target), targetOverrideReason: reason, active } }),
      `${name} saved.`,
    );
    if (ok) onClose();
  });
  return (
    <Modal open title={`Team: ${team.name}`} onClose={onClose} footer={<Footer onClose={onClose} busy={busy} disabled={!name.trim()} label="Save" form="team" />}>
      <form id="team" className="form" onSubmit={submit}>
        <label className="fld">Team name<input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required /></label>
        <fieldset className="fld-group">
          <legend>Team target override</legend>
          <p className="small muted">
            {sum.length ? `From its engineers: ${sum.join(' + ')} = ${sum.reduce((a, b) => a + b, 0)} a day.` : 'No engineers yet.'} Leave blank to use that.
          </p>
          <div className="fld-row">
            <label className="fld narrow">Installs a day<input type="number" min="0" max="20" step="0.5" value={target} onChange={(e) => setTarget(e.target.value)} /></label>
            <label className="fld">Reason<input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={255} disabled={target === ''} required={target !== ''} /></label>
          </div>
        </fieldset>
        <label className="check"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Team is active</label>
        {!active && team.members.length > 0 && <p className="small warn-text">Move its engineers out first; a team with engineers can&apos;t be closed.</p>}
      </form>
    </Modal>
  );
}

// Put someone in a team (moving them out of their old one) from a date.
export function MoveModal({ person, team, from, change, onClose }) {
  const [date, setDate] = useState(todayUk());
  const [busy, submit] = useSubmit(async () => {
    const ok = await change(
      () => api(`/matchups/teams/${team.id}/members`, { method: 'POST', body: { personId: person.id, fromDate: date } }),
      `${person.name} is in ${team.name} from ${formatDay(date)}.`,
    );
    if (ok) onClose();
  });
  return (
    <Modal open title={from ? `Move ${person.name} to ${team.name}?` : `Put ${person.name} in ${team.name}?`} onClose={onClose}
      footer={<Footer onClose={onClose} busy={busy} disabled={!date} label={from ? 'Move' : 'Add to team'} onClick={submit} />}>
      <div className="form">
        <label className="fld narrow">From<input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        {from && <p className="small muted">Their last day in {from.name} is the day before. Both teams&apos; targets change from this date; earlier days keep the old line-up.</p>}
      </div>
    </Modal>
  );
}

export function RemoveModal({ person, team, change, onClose }) {
  const [date, setDate] = useState(todayUk());
  const [busy, submit] = useSubmit(async () => {
    const ok = await change(
      () => api(`/matchups/teams/${team.id}/members/${person.id}?from=${date}`, { method: 'DELETE' }),
      date === todayUk() ? `${person.name} is out of ${team.name}.` : `${person.name} is out of ${team.name} from ${formatDay(date)}.`,
    );
    if (ok) onClose();
  });
  return (
    <Modal open title={`Take ${person.name} out of ${team.name}?`} onClose={onClose} footer={<Footer onClose={onClose} busy={busy} disabled={!date} danger label="Take out" onClick={submit} />}>
      <div className="form">
        <label className="fld narrow">Out of the team from<input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <p className="small muted">Today takes them out straight away, and the team has a free place.</p>
        <p className="small muted">Their past jobs stay with {team.name}. To move them to another team, pick that team in the Team column instead.</p>
      </div>
    </Modal>
  );
}

// Create a team and put this person in it, in one go.
export function NewTeamModal({ person, change, onClose }) {
  const [name, setName] = useState('');
  const [date, setDate] = useState(todayUk());
  const [busy, submit] = useSubmit(async (e) => {
    e.preventDefault();
    const ok = await change(async () => {
      const { id } = await api('/matchups/teams', { method: 'POST', body: { name } });
      await api(`/matchups/teams/${id}/members`, { method: 'POST', body: { personId: person.id, fromDate: date } });
    }, `${name} created with ${person.name} in it.`);
    if (ok) onClose();
  });
  return (
    <Modal open title="New team" onClose={onClose} footer={<Footer onClose={onClose} busy={busy} disabled={!name.trim()} label="Create team" form="newteam" />}>
      <form id="newteam" className="form" onSubmit={submit}>
        <label className="fld">Team name<input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required autoFocus /></label>
        <label className="fld narrow">{person.name} joins from<input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
      </form>
    </Modal>
  );
}

// Band list, edited in place like the main table: type a name or number and
// it saves when you leave the box; tick "In use" off to retire a band. The
// last row adds a new band.
export function BandsModal({ data, canEdit, change, onClose }) {
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ name: '', dailyTarget: '' });
  const count = (id) => data.people.filter((p) => p.active && p.bandId === id).length;

  // Save through the API, say what happened in the pop-up, reload the page data.
  const save = async (fn, okText) => {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      await change(async () => {}, okText);
      setMsg({ tone: 'ok', text: okText });
      return true;
    } catch (e) {
      setMsg({ tone: 'bad', text: problem(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };
  const update = (b, body, okText) => save(() => api(`/matchups/bands/${b.id}`, { method: 'PUT', body }), okText);
  const add = async (e) => {
    e.preventDefault();
    const ok = await save(
      () => api('/matchups/bands', { method: 'POST', body: { name: draft.name, dailyTarget: Number(draft.dailyTarget) } }),
      `Band ${draft.name.trim()} added.`,
    );
    if (ok) setDraft({ name: '', dailyTarget: '' });
  };

  return (
    <Modal open title="Bands" onClose={onClose} footer={<button type="button" className="btn" onClick={onClose}>Close</button>}>
      <p className="muted small">
        A band sets an engineer&apos;s daily install target; a team&apos;s target is its two engineers&apos; added together.
        {canEdit && ' Click a name or number to change it.'}
      </p>
      {msg && <p className={`notice-line notice-${msg.tone}`} role="status">{msg.text}</p>}
      <table className="table bands">
        <thead><tr><th>Band</th><th>Installs a day</th><th>People</th><th>In use</th></tr></thead>
        <tbody>
          {data.bands.map((b) => (
            <tr key={b.id} className={b.active ? '' : 'inactive'}>
              <td data-label="Band">
                {canEdit
                  ? <InlineInput value={b.name} label={`Name of ${b.name}`} maxLength={60} disabled={busy}
                      onSave={(v) => (v ? update(b, { name: v }, `Band renamed ${v}.`) : setMsg({ tone: 'bad', text: 'A band needs a name.' }))} />
                  : <strong>{b.name}</strong>}
              </td>
              <td data-label="Installs a day">
                {canEdit
                  ? <InlineInput type="number" value={String(b.dailyTarget)} label={`Installs a day for ${b.name}`} disabled={busy}
                      onSave={(v) => update(b, { dailyTarget: Number(v) }, `${b.name} is now ${v} a day.`)} />
                  : b.dailyTarget}
              </td>
              <td data-label="People">{count(b.id)}</td>
              <td data-label="In use">
                <input type="checkbox" role="switch" className="switch" checked={b.active} disabled={!canEdit || busy} aria-label={`${b.name} in use`}
                  onChange={(e) => update(b, { active: e.target.checked }, `${b.name} ${e.target.checked ? 'is back in use' : 'retired'}.`)} />
              </td>
            </tr>
          ))}
          {canEdit && (
            <tr className="add-row">
              <td data-label="New band">
                <input className="cell-input wide" form="add-band" placeholder="New band name" value={draft.name} maxLength={60} aria-label="New band name"
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
              </td>
              <td data-label="Installs a day">
                <input className="cell-input" form="add-band" type="number" min="0" max="20" step="0.5" placeholder="e.g. 3" value={draft.dailyTarget} aria-label="Installs a day for the new band"
                  onChange={(e) => setDraft((d) => ({ ...d, dailyTarget: e.target.value }))} />
              </td>
              <td colSpan={2}>
                <form id="add-band" onSubmit={add}>
                  <button type="submit" className="btn primary" disabled={busy || !draft.name.trim() || draft.dailyTarget === ''}>Add band</button>
                </form>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {canEdit && <p className="small muted">Changing a number changes the target for everyone on that band. A band can only be retired once nobody is on it.</p>}
    </Modal>
  );
}

// A value edited in place: saves on Enter or leaving the box, only if it
// changed; Escape puts it back.
function InlineInput({ value, onSave, label, type = 'text', maxLength, disabled }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  const commit = () => {
    const t = v.trim();
    if (t !== value) onSave(t);
  };
  return (
    <input className={`cell-input${type === 'text' ? ' wide' : ''}`} type={type} value={v} aria-label={label} maxLength={maxLength} disabled={disabled}
      min={type === 'number' ? 0 : undefined} max={type === 'number' ? 20 : undefined} step={type === 'number' ? 0.5 : undefined}
      onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setV(value); }} />
  );
}


export function HistoryModal({ onClose }) {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api('/matchups/history').then((r) => setEntries(r.entries), (e) => setError(problem(e)));
  }, []);
  return (
    <Modal open title="Match-Ups history" onClose={onClose} footer={<button type="button" className="btn" onClick={onClose}>Close</button>}>
      <p className="muted small">The last 100 changes to people, teams and bands.</p>
      {error && <p className="notice-line notice-bad">{error}</p>}
      {!error && !entries && <p className="muted">Loading…</p>}
      {entries && !entries.length && <p className="muted">No changes yet.</p>}
      {entries?.length > 0 && (
        <ul className="history">
          {entries.map((e) => (
            <li key={e.id}>
              <span>{e.summary}</span>
              <span className="muted small">{formatDateTime(e.changedAt)} · {e.actorEmail}</span>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
